import { Question, AssessmentData, BodyTypeResult, DoshaScores } from '../types';

export function calculateDoshaScores(assessmentData: AssessmentData): DoshaScores {
  const scores: DoshaScores = {
    vata: 0,
    pitta: 0,
    kapha: 0
  };

  // Count responses for each dosha (A=Vata, B=Pitta, C=Kapha as per FINAL.md)
  Object.values(assessmentData.responses).forEach(response => {
    if (response && response.dosha) {
      scores[response.dosha]++;
    }
  });

  return scores;
}

export function determineBodyType(scores: DoshaScores): string {
  const { vata, pitta, kapha } = scores;
  const total = vata + pitta + kapha;
  
  // Calculate percentages as per FINAL.md guidelines
  const vataPercent = (vata / total) * 100;
  const pittaPercent = (pitta / total) * 100;
  const kaphaPercent = (kapha / total) * 100;

  // Step 2: Determine Constitutional Type as per FINAL.md
  
  // Single Dosha Dominant (Mono-types) - 60% or higher
  if (vataPercent >= 60) return 'vata';
  if (pittaPercent >= 60) return 'pitta';
  if (kaphaPercent >= 60) return 'kapha';

  // Dual Dosha Types (Bi-doshic) - both higher than the third
  // Vata-Pitta: Vata and Pitta scores are close, both higher than Kapha
  if (vata > kapha && pitta > kapha) {
    return vata > pitta ? 'vata-pitta' : 'pitta-vata';
  }
  
  // Pitta-Kapha: Pitta and Kapha scores are close, both higher than Vata
  if (pitta > vata && kapha > vata) {
    return pitta > kapha ? 'pitta-kapha' : 'kapha-pitta';
  }
  
  // Kapha-Vata: Kapha and Vata scores are close, both higher than Pitta
  if (kapha > pitta && vata > pitta) {
    return kapha > vata ? 'kapha-vata' : 'vata-kapha';
  }

  // Triple Dosha Type (Tri-doshic) - all three scores are relatively equal
  // This is rare and occurs when no clear dominance pattern exists
  return 'tridoshic';
}

export function generateBodyTypeResult(scores: DoshaScores): BodyTypeResult {
  const bodyType = determineBodyType(scores);
  const total = scores.vata + scores.pitta + scores.kapha;
  
  const percentages = {
    vata: Math.round((scores.vata / total) * 100),
    pitta: Math.round((scores.pitta / total) * 100),
    kapha: Math.round((scores.kapha / total) * 100)
  };

  return {
    bodyType,
    scores,
    percentages,
    description: getBodyTypeDescription(bodyType),
    recommendations: getBodyTypeRecommendations(bodyType)
  };
}

function getBodyTypeDescription(bodyType: string): string {
  const descriptions: Record<string, string> = {
    'vata': 'Vata constitution embodies the qualities of Air and Space - light, dry, cold, rough, subtle, and mobile. Vata types tend to be creative, quick-thinking, and adaptable, but may experience anxiety, irregular digestion, and difficulty maintaining routine.',
    'pitta': 'Pitta constitution embodies Fire and Water elements - hot, sharp, light, liquid, and oily. Pitta types are typically intelligent, focused, and driven, but may be prone to irritability, inflammation, and overheating.',
    'kapha': 'Kapha constitution represents Earth and Water - heavy, slow, cool, oily, smooth, and stable. Kapha types are generally calm, loyal, and strong, but may struggle with weight gain, sluggishness, and resistance to change.',
    'vata-pitta': 'Vata-Pitta constitution combines the creative, adaptable nature of Vata with the focused, driven qualities of Pitta. This creates a dynamic personality that can be both innovative and goal-oriented, though it requires careful balance.',
    'pitta-vata': 'Pitta-Vata constitution emphasizes the focused, analytical nature of Pitta with the creative, adaptable qualities of Vata. This combination creates a balanced approach to both planning and flexibility.',
    'pitta-kapha': 'Pitta-Kapha constitution combines the focused, driven nature of Pitta with the stable, nurturing qualities of Kapha. This creates a reliable personality that can be both ambitious and patient.',
    'kapha-pitta': 'Kapha-Pitta constitution emphasizes the stable, nurturing nature of Kapha with the focused, driven qualities of Pitta. This combination creates a balanced approach to both consistency and achievement.',
    'kapha-vata': 'Kapha-Vata constitution combines the stable, nurturing nature of Kapha with the creative, adaptable qualities of Vata. This creates a personality that can be both grounded and innovative.',
    'vata-kapha': 'Vata-Kapha constitution emphasizes the creative, adaptable nature of Vata with the stable, nurturing qualities of Kapha. This combination creates a balanced approach to both flexibility and consistency.',
    'tridoshic': 'Tridoshic constitution represents a rare and harmonious balance of all three doshas. This creates a highly adaptable personality that can thrive in various conditions, though it requires careful attention to maintain equilibrium and adapt to seasonal changes.'
  };

  return descriptions[bodyType] || 'A unique constitutional type with balanced characteristics.';
}

function getBodyTypeRecommendations(bodyType: string): string[] {
  const recommendations: Record<string, string[]> = {
    'vata': [
      'Maintain regular daily routines and meal times',
      'Favor warm, cooked, moist foods',
      'Practice grounding exercises like walking and gentle yoga',
      'Use warming oils like sesame for self-massage',
      'Avoid cold, dry, and raw foods',
      'Practice meditation and breathing exercises for mental stability'
    ],
    'pitta': [
      'Stay cool and avoid excessive heat exposure',
      'Favor cooling foods like cucumbers and leafy greens',
      'Practice moderate exercise during cooler parts of the day',
      'Use cooling oils like coconut for self-massage',
      'Avoid spicy, hot, and sour foods',
      'Practice cooling breathing techniques and meditation'
    ],
    'kapha': [
      'Engage in regular, vigorous exercise',
      'Favor light, dry, and warming foods',
      'Wake up early and avoid daytime napping',
      'Use stimulating spices like ginger and black pepper',
      'Avoid heavy, oily, and sweet foods',
      'Practice energizing activities and maintain variety'
    ],
    'vata-pitta': [
      'Maintain regular routines while avoiding overheating',
      'Favor warm, moist foods that are not too spicy',
      'Practice moderate exercise in cool environments',
      'Balance creativity with structure',
      'Avoid both cold foods and excessive heat',
      'Practice grounding yet cooling meditation techniques'
    ],
    'pitta-vata': [
      'Focus on cooling practices while maintaining routine',
      'Favor moderate temperatures and balanced foods',
      'Practice focused exercise without overexertion',
      'Balance analytical thinking with creativity',
      'Avoid extreme temperatures and irregular schedules',
      'Practice systematic yet flexible approaches'
    ],
    'pitta-kapha': [
      'Balance stimulation with cooling practices',
      'Favor light, cooling foods in moderate portions',
      'Practice regular exercise during cooler times',
      'Maintain drive while cultivating patience',
      'Avoid heavy foods and excessive heat',
      'Practice focused yet steady approaches'
    ],
    'kapha-pitta': [
      'Emphasize stimulation while staying cool',
      'Favor energizing foods that are not too heavy',
      'Practice vigorous exercise in cool environments',
      'Balance ambition with stability',
      'Avoid sluggishness and overheating',
      'Practice dynamic yet consistent approaches'
    ],
    'kapha-vata': [
      'Balance grounding with stimulation',
      'Favor warming foods in moderate portions',
      'Practice regular exercise without overexertion',
      'Maintain stability while embracing change',
      'Avoid both stagnation and excessive movement',
      'Practice steady yet flexible approaches'
    ],
    'vata-kapha': [
      'Balance creativity with grounding',
      'Favor nourishing foods that are not too heavy',
      'Practice gentle exercise with consistency',
      'Maintain flexibility while building routine',
      'Avoid both instability and rigidity',
      'Practice adaptable yet stable approaches'
    ],
    'tridoshic': [
      'Adapt practices based on current season and needs',
      'Maintain variety in diet and exercise',
      'Pay attention to which dosha becomes imbalanced first',
      'Practice seasonal adjustments to routine',
      'Avoid extremes in any direction',
      'Practice balanced and adaptable approaches'
    ]
  };

  return recommendations[bodyType] || [
    'Maintain balance through regular routine',
    'Adapt practices based on current needs',
    'Practice moderation in all activities'
  ];
}